import { Achievement } from '../models/Achievement.js'
import { Challenge } from '../models/Challenge.js'
import { Difficulty } from '../models/Difficulty.js'
import { GameConfig } from '../models/GameConfig.js'
import {
  ChallengeScore,
  GameScore,
  RewardScore,
  TeamScore,
} from '../models/GameScore.js'
import { BaseReward, Reward } from '../models/Reward.js'
import { Team } from '../models/Team.js'

export function computeGameScore(
  achievements: Achievement[],
  rewards: Reward[],
  challenges: Challenge[],
  teams: Team[],
  config: GameConfig,
): GameScore {
  const challsScore = computeChallenges(challenges, achievements, config)
  const rewardsScore = computeRewards(rewards, config)
  const teamsScore = computeTeams(teams, challsScore, rewardsScore)

  const scorersNotOnPodium = teamsScore.filter(s => s.score > 0 && s.rank > 3)
  const beforeLastScorer =
    scorersNotOnPodium.length > 2
      ? scorersNotOnPodium[scorersNotOnPodium.length - 2]
      : undefined

  return {
    challsScore,
    teamsScore,
    beforeLastScorer,
    config: {
      ...config,
    },
  }
}

//#region challenge score
function computeChallenges(
  challenges: Challenge[],
  achievements: Achievement[],
  config: GameConfig,
): Record<string, ChallengeScore> {
  return challenges.reduce<Record<string, ChallengeScore>>(
    (agg, challenge) => ({
      ...agg,
      [challenge._id]: computeChallenge(achievements, challenge, config),
    }),
    {},
  )
}

function computeChallenge(
  achievements: Achievement[],
  challenge: Challenge,
  config: GameConfig,
): ChallengeScore {
  const a = achievements
    // we need to keep simple equality check because on server side, we haven't a string but a Mongoose ObjectId
    .filter(a => a.challengeId == challenge._id)
    .sort((x, y) => x.createdAt.getTime() - y.createdAt.getTime())

  return {
    challenge,
    score: computeChallengeScore(challenge, config, a.length),
    achievements: a,
  }
}

function computeChallengeScore(
  challenge: Challenge,
  {
    baseChallScore,
    isDifficultyIncluded,
    isNoCompetition,
    teamCount,
  }: GameConfig,
  solvedCount: number = 0,
): number {
  return isNoCompetition
    ? 0
    : isDifficultyIncluded
      ? baseChallScore *
        DifficultyValue[challenge.difficulty] *
        (teamCount - solvedCount)
      : baseChallScore * (teamCount - solvedCount)
}

const DifficultyValue: Record<Difficulty, number> = {
  special: 0,
  easy: 1,
  medium: 2,
  hard: 3,
}
//#endregion

//#region reward score
function computeRewards(rewards: Reward[], config: GameConfig): RewardScore[] {
  return rewards.map<RewardScore>(r => ({
    reward: r,
    score: computeRewardScore(r, config),
  }))
}

export function computeRewardScore(
  reward: BaseReward,
  { isRewardProportional, isNoCompetition, teamCount }: GameConfig,
): number {
  return isNoCompetition
    ? 0
    : isRewardProportional
      ? reward.value * teamCount
      : reward.value
}
//#endregion

//#region team score
function computeTeams(
  teams: Team[],
  challsScore: Record<string, ChallengeScore>,
  rewards: RewardScore[],
): TeamScore[] {
  return teams
    .map(t => computeTeam(challsScore, t, rewards))
    .sort((a, b) =>
      b.score - a.score !== 0
        ? b.score - a.score
        : b.breakthroughs.length - a.breakthroughs.length !== 0
          ? b.breakthroughs.length - a.breakthroughs.length
          : a.team.name.localeCompare(b.team.name),
    )
    .map((ts, i, tss) => ({
      ...ts,
      rank: tss.findIndex(x => x.score === ts.score) + 1,
    }))
}

function computeTeam(
  challsScore: Record<string, ChallengeScore>,
  team: Team,
  rewards: RewardScore[],
): TeamScore {
  const challengeResolved = Object.values(challsScore).filter(cs =>
    cs.achievements.find(a => a.teamId === team._id),
  )
  const rewardAcquired = rewards.filter(r => r.reward.teamId === team._id)

  return {
    rank: 0,
    team,
    score: computeTeamScore(challengeResolved, rewardAcquired),
    breakthroughs: challengeResolved
      .map(cs => cs.achievements[0] ?? ({ teamId: '' } as Achievement))
      .filter(a => a.teamId === team._id),
    solved: challengeResolved
      .flatMap(cs => cs.achievements)
      .filter(a => a.teamId === team._id),
    rewards: rewardAcquired,
  }
}

function computeTeamScore(
  challsScore: ChallengeScore[],
  rewards: RewardScore[],
): number {
  return (
    challsScore.reduce((agg, cs) => agg + cs.score, 0) +
    rewards.reduce((acc, cur) => acc + cur.score, 0)
  )
}
//#endregion
